import { useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import ProgressBar from "../../components/widgets/ProgressBar";
import TextInput from "../../components/forms/TextInput";
import ChoiceGroup from "../../components/forms/ChoiceGroup";
import ModalBase from "../../components/modals/ModalBase";
import { useStore } from "../../js/state/store";
import { preferenceProgress, summarizeChecklist } from "../../js/utils/formatters";
import { updateBuyer } from "../../js/api/buyersApi";
import { validateBuyerAccount } from "../../js/utils/validators";

export default function BuyerComplete() {
  const { state, dispatch } = useStore();
  const buyer = state.buyer.current;
  const progress = preferenceProgress(buyer.preferences);
  const summary = summarizeChecklist(buyer.preferences);
  const submitted = Boolean(buyer.preferences?.submission?.confirmed);

  const [contact, setContact] = useState({
    name: buyer.name || "",
    phone: buyer.phone || "",
    email: buyer.email || "",
    contactMethod: buyer.preferences?.submission?.contactMethod || "email",
  });
  const [errors, setErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState(
    submitted ? "This checklist was already submitted to your agent." : ""
  );

  function update(event) {
    const { name, value } = event.target;
    setContact((current) => ({ ...current, [name]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const nextErrors = validateBuyerAccount(contact);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const next = {
      ...buyer,
      name: contact.name,
      phone: contact.phone,
      email: contact.email,
      status: "searching",
      preferences: {
        ...buyer.preferences,
        submission: {
          confirmed: true,
          contactMethod: contact.contactMethod,
          submittedAt: new Date().toISOString(),
        },
      },
    };
    dispatch({ type: "SET_BUYER", payload: next });
    if (next.id) {
      await updateBuyer(next.id, next);
    }
    setMessage("Your DreamHome Checklist was submitted to your agent.");
    setModalOpen(true);
  }

  return (
    <AppLayout variant="buyer" showBuyerNav>
      <section className="card sendoff-banner" data-tour="buyer-end">
        <h1>All Done! Time for Send-off.</h1>
        <div className="title-rule" />
        <p className="page-lead">
          Please be sure you have thoroughly reviewed your choices, then off to your Agent.
        </p>
      </section>

      {message ? <p className="confirm-banner">{message}</p> : null}

      <div className="card" style={{ marginBottom: "1rem" }}>
        <ProgressBar percent={progress.percent} label="Checklist completeness" />
      </div>

      <form className="card" onSubmit={handleSubmit} style={{ marginBottom: "1rem" }}>
        <h2>How Should Your Agent Reach You?</h2>
        <TextInput label="Buyer name" name="name" value={contact.name} onChange={update} error={errors.name} required />
        <div className="two-col">
          <TextInput label="Phone" name="phone" value={contact.phone} onChange={update} error={errors.phone} />
          <TextInput label="Email" name="email" type="email" value={contact.email} onChange={update} error={errors.email} required />
        </div>
        <ChoiceGroup
          label="Preferred contact method"
          name="contactMethod"
          value={contact.contactMethod}
          onChange={update}
          options={[
            { value: "email", label: "Email" },
            { value: "phone", label: "Phone call" },
            { value: "text", label: "Text" },
          ]}
        />
        <button type="submit" className="primary-btn">
          Send to my agent
        </button>
      </form>

      <section className="card">
        <h2>Summary Preview</h2>
        {summary.length ? (
          summary.map((block) => (
            <div key={block.title} className="checklist-section">
              <h3>{block.title}</h3>
              <ul className="summary-list">
                {block.lines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          ))
        ) : (
          <p className="muted">No selections yet. Walk through the checklist, then come back to submit.</p>
        )}
        <div className="row" style={{ marginTop: "1rem" }}>
          <Link to="/start" className="ghost-btn">
            Back to start
          </Link>
          <Link to="/dashboard" className="text-btn">
            Main Dashboard
          </Link>
        </div>
      </section>

      <ModalBase isOpen={modalOpen} title="Checklist submitted" onClose={() => setModalOpen(false)}>
        <p>
          Thank you, {contact.name || "buyer"}. Your DreamHome Checklist was sent to your agent.
          They can review your preferences in Client Manager and follow up by{" "}
          {contact.contactMethod || "email"}.
        </p>
        <p className="muted">Keep using the transaction checklist as escrow milestones come up.</p>
        <button type="button" className="primary-btn btn-block" onClick={() => setModalOpen(false)}>
          Done
        </button>
      </ModalBase>
    </AppLayout>
  );
}
